import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, map, Observable } from 'rxjs';
import {
  addContact,
  addInstitution,
  logout,
  openContactSupportDrawer,
  showCookieSettings
} from '@capital-access/common/actions';
import { CookieSettingsService } from '@capital-access/common/components';
import { LocalizationService } from '@capital-access/common/localization';
import {
  EntityNavItemConfig,
  NavigationConfigService,
  ToggleNavItemConfig,
  UserNavItemConfig,
  UserProfileDropdownItem
} from '@capital-access/common/navigation';
import {
  getSelectedSecurity,
  getSelectedSecuritySurveillance,
  toggleSurveillance
} from '@capital-access/common/security';
import { getContentPermissions, getUser, User } from '@capital-access/common/user';
import { addNewList } from '@capital-access/lists/common';
import { EntityItem } from '../../models/entity-item';
import { CONTACT_SUPPORT_URL, COOKIE_SETTINGS_URL, LOGOUT_URL } from './../navigation.constants';

@Injectable()
export class SharedNavigationItemsService {
  localizationScope = 'navigation';

  entitySettingsIcon = 'f-i-addressbook-add';

  entitySettingsOptionsTop: EntityItem[] = [
    { localizationKey: 'addNewInstitution', action: addInstitution() },
    { localizationKey: 'addNewContact', action: addContact() },
    { localizationKey: 'addNewList', action: addNewList() }
  ];
  entitySettingsOptionsSide: EntityNavItemConfig[] = [
    { ...this.entitySettingsOptionsTop[0], icon: this.entitySettingsIcon },
    ...this.entitySettingsOptionsTop.slice(1)
  ];

  showToggleComponent$ = combineLatest([
    this.store.select(getContentPermissions),
    this.store.select(getSelectedSecurity)
  ]).pipe(
    map(
      ([permissions, securityId]) => !!(securityId?.id && permissions?.surveillance.securityIds.includes(securityId.id))
    )
  );

  toggleSettingsOption: ToggleNavItemConfig[] = [
    {
      localizationKey: 'surveillance',
      action: toggleSurveillance(),
      value: this.store.select(getSelectedSecuritySurveillance),
      isShown: this.showToggleComponent$
    }
  ];

  user$: Observable<User | null>;

  userSettingsOptionsTop$: Observable<UserProfileDropdownItem[]>;
  userSettingsOptionsSide$: Observable<UserNavItemConfig[]>;

  constructor(
    private store: Store,
    private localizationService: LocalizationService,
    navigationConfig: NavigationConfigService,
    private cookieSettingsService: CookieSettingsService
  ) {
    this.user$ = this.store.select(getUser);
    const routes = navigationConfig.userSettingsRoutes;

    // Base user settings options without cookie settings
    const baseUserSettingsOptions = [
      { localizationKey: 'myAccount', link: routes?.myAccount || '' },
      { localizationKey: 'contactSupport', action: openContactSupportDrawer(), link: CONTACT_SUPPORT_URL },
      { localizationKey: 'settings', link: routes?.settings || '' }
    ]; // Cookie settings option (conditionally added based on feature flag)
    const cookieSettingsOption = {
      localizationKey: 'cookieSettings',
      action: showCookieSettings(),
      link: COOKIE_SETTINGS_URL
    };

    // Logout option
    const logoutOption = { localizationKey: 'logOut', action: logout(), link: LOGOUT_URL };

    // Create observable for user settings options with cookie banner support
    const userSettingsOptions$ = this.cookieSettingsService
      .shouldShowCookieBanner()
      .pipe(
        map(cookieBannerEnabled => [
          ...baseUserSettingsOptions,
          ...(cookieBannerEnabled ? [cookieSettingsOption] : []),
          logoutOption
        ])
      );

    this.userSettingsOptionsTop$ = combineLatest([
      this.localizationService.getLocalization(this.localizationScope),
      userSettingsOptions$
    ]).pipe(
      map(([localization, userSettingsOptions]) => {
        return userSettingsOptions.map(option => {
          return {
            text:
              localization[option.localizationKey] ??
              (option.localizationKey === 'cookieSettings' ? 'Cookie Settings' : option.localizationKey),
            route: option.link
          };
        });
      })
    );

    this.userSettingsOptionsSide$ = combineLatest([this.user$, userSettingsOptions$]).pipe(
      map(([user, userSettingsOptions]) => {
        return [{ ...userSettingsOptions[0], iconText: this.getInitials(user) }, ...userSettingsOptions.slice(1)];
      })
    );
  }

  private getInitials(user: User | null): string {
    if (!user) return '-';
    const firstInitial = user.firstName && user.firstName?.trim()[0];
    const lastInitial = user.lastName && user.lastName?.trim()[0];
    if (firstInitial && lastInitial) {
      return firstInitial + lastInitial;
    } else if (!firstInitial && !lastInitial) {
      return '-';
    } else {
      return (firstInitial as string) || (lastInitial as string);
    }
  }
}
